import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CircleNotch } from "@phosphor-icons/react";
import { AdminHeader, PrimaryButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { SyncOverview } from "@/components/admin/production/SyncOverview";
import { RulebookTab } from "@/components/admin/rules/RulebookTab";
import { ColumnSettings } from "@/components/admin/production/ColumnSettings";

const TABS = [["overview", "Overview"], ["rules", "Rules Assistant"], ["columns", "Columns & Settings"]];

export default function AdminProductionSync() {
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const wasRunning = useRef(false);

  const loadStatus = useCallback(() => {
    adminApi.get("/admin/production/status").then(({ data }) => {
      if (wasRunning.current && !data.running && data.latest_run) {
        const ok = data.latest_run.status === "success";
        (ok ? toast.success : toast.error)(data.latest_run.message || "Sync finished");
      }
      wasRunning.current = data.running;
      setStatus(data);
    }).catch((e) => toast.error(getErrorMessage(e, "Couldn't load sync status")));
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setInterval(loadStatus, 3000);
    return () => clearInterval(t);
  }, [status?.running, loadStatus]);

  const start = async (path) => {
    try {
      await adminApi.post(path);
      wasRunning.current = true;
      setStatus((s) => (s ? { ...s, running: true } : s));
      setTimeout(loadStatus, 1200);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const clearPrefill = useCallback(() => setPrefill(null), []);
  const running = !!status?.running;

  return (
    <div>
      <AdminHeader
        title="Production Sync"
        subtitle="Imports new vans from the OTM Feishen production schedule (Monday.com) using your rulebook"
        actions={
          <PrimaryButton onClick={() => start("/admin/production/sync")} disabled={running} data-testid="production-sync-now">
            {running ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} />} {running ? "Syncing…" : "Sync now"}
          </PrimaryButton>
        }
      />
      <div className="p-8">
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`production-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === k ? "bg-[#73bf44] text-[#050505]" : "border border-neutral-800 text-neutral-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        {tab === "overview" && (
          <SyncOverview status={status} onAskAi={(text) => { setPrefill(text); setTab("rules"); }} onGoTo={setTab} />
        )}
        {tab === "rules" && <RulebookTab prefill={prefill} onPrefillUsed={clearPrefill} onChanged={loadStatus} />}
        {tab === "columns" && <ColumnSettings onChanged={loadStatus} />}
      </div>
    </div>
  );
}
